import logger from 'logger';

import { getAuthClient } from './clients';

type AuthUser = {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string | null;
  initials: string;
  creditInCents: number;
};

export async function signup(input: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  recaptchaToken: string;
  referralCode?: string;
  signInLocation?: string;
}): Promise<
  | { ok: true; user: AuthUser }
  | {
      ok: false;
      reason: 'validation';
      fieldErrors: {
        email?: string;
        password?: string;
        firstName?: string;
        lastName?: string;
      };
    }
  | { ok: false; reason: 'blocked' }
  | { ok: false; reason: 'unknown' }
> {
  const client = getAuthClient();

  let res;
  try {
    res = await client.post<{
      success: boolean;
      data: AuthUser;
      error: {
        code: number | null;
        message: string | null;
        validation: Record<string, string[]> | null;
      };
    }>('/signup', {
      email: input.email,
      password: input.password,
      first_name: input.firstName,
      last_name: input.lastName,
      recaptcha_token: input.recaptchaToken,
      referral_code: input.referralCode,
      sign_in_location: input.signInLocation,
    });
  } catch (err: any) {
    const status = err?.response?.status;
    if (status === 422) {
      const validation = err?.response?.data?.error?.validation ?? {};
      return {
        ok: false,
        reason: 'validation',
        fieldErrors: {
          email: validation.email?.[0],
          password: validation.password?.[0],
          firstName: validation.first_name?.[0],
          lastName: validation.last_name?.[0],
        },
      };
    }
    // 401 = recaptcha rejected (missing or invalid token). Bot traffic, not
    // worth logging as an error.
    if (status === 401) {
      return { ok: false, reason: 'blocked' };
    }
    logger.error({
      msg: 'SignupRequestFailed',
      noticable: err,
      extra: { context: { status } },
    });
    return { ok: false, reason: 'unknown' };
  }

  if (!res.data?.success || !res.data?.data) {
    const err = new Error('signup response did not include user data');
    logger.error({
      msg: 'SignupNonOkResponse',
      noticable: err,
    });
    return { ok: false, reason: 'unknown' };
  }

  return { ok: true, user: res.data.data };
}
